import { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import useEntityStore from '../../../stores/entityStore';
import { entityResolver } from '../../../services/entityResolver';

export const apiConnectionReadCommand: SelfContainedCommand = {
  id: 'api_connection_read',
  description: `Read API connection documentation before making API calls.

**IMPORTANT:** Call this BEFORE using api_connect to understand:
- Available endpoints and methods
- Required parameters
- Authentication details
- Rate limits and best practices

Returns the serviceProfile (API documentation) for the connection.

**Usage:**
\`\`\`json
{
  "connectionId": "apiconnection_aurora_stellar_cjdrop"
}
\`\`\`

**When to use:**
- Before first api_connect call to a new API
- When getting endpoint errors
- When unsure about API structure`,

  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'connectionId',
        type: 'string',
        description: 'ID, llmId, or name of the APIConnection to read',
        required: true,
      },
    ],
  },
  
  examples: {
    good: [
      {
        input: {
          connectionId: 'apiconnection_aurora_stellar_cjdrop',
        },
        expectedOutput: {
          status: 'success',
          message: 'CJDropshipping API documentation',
          format: 'json',
          data: {
            serviceName: 'CJDropshipping',
            baseUrl: 'https://developers.cjdropshipping.com/api2.0/v1',
            authType: 'api_key',
            serviceProfile: 'CJDropshipping API...',
          },
        },
        description: 'Read API documentation before making calls',
      },
    ],
    bad: [
      {
        input: {
          connectionId: 'invalid_connection',
        },
        error: 'APIConnection not found',
        why: 'Connection does not exist',
        fix: 'Use integration_list to find available connections',
      },
    ],
  },
  
  handler: async (params, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { connectionId } = params;
    
    if (!connectionId) {
      return createErrorResult(
        'connectionId is required',
        'Provide the ID of the APIConnection to read',
        'validation'
      );
    }
    
    try {
      console.log('[api_connection_read] 🔍 Reading connection:', connectionId);
      
      let connection: any;
      
      // Try to resolve via entityResolver first
      try {
        const resolved = await entityResolver.resolveEntity(connectionId, context);
        connection = resolved.entity;
        
        if (connection.entityType !== 'APIConnection') {
          return createErrorResult(
            `Not an APIConnection: ${connectionId}`,
            `Entity is of type ${connection.entityType}, expected APIConnection`,
            'validation'
          );
        }
      } catch (error: any) {
        // Fallback to name-based lookup
        const entityStore = useEntityStore.getState();
        const apiConnections = entityStore.entities.filter(e => e.entityType === 'APIConnection');
        
        connection = apiConnections.find(e => 
          e._id === connectionId ||
          e.llmId === connectionId ||
          e.name?.toLowerCase() === connectionId.toLowerCase()
        );
        
        if (!connection) {
          return createErrorResult(
            'APIConnection not found',
            `No APIConnection found with ID: ${connectionId}`,
            'validation'
          );
        }
      }
      
      const skeleton = connection.skeleton;
      const serviceProfile = skeleton.serviceProfile;
      
      if (!serviceProfile) {
        return createErrorResult(
          'No service profile available',
          `Connection ${connection.name} does not have API documentation`,
          'validation'
        );
      }
      
      console.log('[api_connection_read] ✅ Found serviceProfile:', serviceProfile.length, 'chars');
      
      // Return the API documentation
      return createSuccessResult(
        `API Documentation for ${connection.name}`,
        {
          connectionId: connection._id,
          llmId: connection.llmId,
          serviceName: skeleton.serviceName,
          baseUrl: skeleton.baseUrl,
          authType: skeleton.authType,
          docsUrl: skeleton.docsUrl,
          serviceProfile: serviceProfile,
          
          // Usage info
          status: skeleton.status,
          lastUsedAt: skeleton.lastUsedAt,
          usageCount: skeleton.usageCount || 0,
          
          // Helpful reminders
          notes: [
            'Read the serviceProfile carefully for correct endpoints and parameters',
            'Pay attention to HTTP methods (GET vs POST)',
            'Check required vs optional parameters',
            'Note any rate limits or special requirements'
          ]
        },
        {
          metadata: {
            affectedEntities: [connection._id],
          },
        }
      );
      
    } catch (error: any) {
      console.error('[api_connection_read] Error:', error);
      return createErrorResult(
        `Failed to read API connection: ${error.message}`,
        error,
        'execution'
      );
    }
  },
};
